import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, CartesianGrid,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/**
 * ImpactAreaByProgramChart
 *
 * Horizontal bar chart showing which Science Programs (SP01, SP02, ...)
 * contribute the most results for a given Impact Area.  Bars are coloured
 * with the IA brand colour and sorted by count descending.
 */
const ImpactAreaByProgramChart = memo(function ImpactAreaByProgramChart({ results, color }) {
  const { chartData, exportData } = useMemo(() => {
    if (!results || results.length === 0) return { chartData: [], exportData: [] };

    const counts = {};
    for (const r of results) {
      const prog = r.initiative;
      if (!prog) continue;
      counts[prog] = (counts[prog] || 0) + 1;
    }

    const sorted = Object.entries(counts)
      .filter(([program]) => /^(SP\d|INIT-|SGP-)/i.test(program))
      .map(([program, count]) => ({ program, count }))
      .sort((a, b) => b.count - a.count);

    const _exportData = sorted.map(d => ({
      Program: d.program,
      'Result Count': d.count,
    }));

    return { chartData: sorted, exportData: _exportData };
  }, [results]);

  if (!chartData || chartData.length === 0) {
    return (
      <ChartWrapper title="Results by Program">
        <NoChartData
          message="No data available"
          detail="No program data found for results in this Impact Area."
        />
      </ChartWrapper>
    );
  }

  const barHeight = Math.max(280, chartData.length * 38);

  return (
    <ChartWrapper
      title="Results by Program"
      info="Number of results contributed by each Science Program for this Impact Area. Only Principal and Significant results are counted."
      chartData={exportData}
      sourceResults={results}
      exportFilename="impact-area-by-program"
    >
      <ResponsiveContainer width="100%" height={barHeight}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 40, left: 8, bottom: 4 }}
          barCategoryGap="24%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="program"
            tick={{ fontSize: 11 }}
            width={60}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{
                  background: '#fff',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: '13px',
                  lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.program}</div>
                  <div style={{ color: '#4b5563' }}>
                    {d.count.toLocaleString()} result{d.count !== 1 ? 's' : ''}
                  </div>
                </div>
              );
            }}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />
          <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]}>
            <LabelList
              dataKey="count"
              position="right"
              style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});

ImpactAreaByProgramChart.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default ImpactAreaByProgramChart;
